<template>
  <div class="bg-black text-white min-h-screen py-4 sm:py-6 md:py-8 px-4 sm:px-6">
    <div class="max-w-7xl mx-auto">
      <!-- Header -->
      <div class="text-center mb-6 sm:mb-8 border-b-2 border-yellow-500 pb-6">
        <h1 class="text-2xl sm:text-4xl md:text-5xl font-bold mb-2 sm:mb-3 text-yellow-500">TORNEO EN VIVO</h1>
        <p class="text-sm sm:text-base md:text-lg text-yellow-400">Pantalla Pública - Se actualiza en tiempo real</p>
        <p class="text-xs sm:text-sm mt-2 sm:mt-4 text-gray-400">⏱️ Última actualización: {{ ultimaActualizacion }}</p>
      </div>

      <!-- Selectores de Torneo y Categoría -->
      <div class="mb-8 p-4 bg-gray-900 rounded-lg border-2 border-yellow-500">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- Selector Torneo -->
          <div>
            <label class="block text-sm font-semibold text-yellow-400 mb-2">🏆 Torneo</label>
            <select
              v-model="torneoSeleccionado"
              @change="cargarBracket"
              class="form-select text-black w-full"
            >
              <option value="">Seleccionar torneo...</option>
              <option v-for="t in torneos" :key="t.id" :value="t.id">
                {{ t.nombre }}
              </option>
            </select>
          </div>

          <!-- Selector Categoría -->
          <div>
            <label class="block text-sm font-semibold text-yellow-400 mb-2">🥋 Categoría</label>
            <select
              v-model="categoriaSeleccionada"
              @change="cargarBracket"
              class="form-select text-black w-full"
              :disabled="!torneoSeleccionado"
            >
              <option value="">Seleccionar categoría...</option>
              <option v-for="c in categoriasDisponibles" :key="c.categoria_id" :value="c.categoria_id">
                {{ c.nombre }}
              </option>
            </select>
          </div>
        </div>
      </div>


      <!-- Loading -->
      <Loading v-if="loading" message="Cargando bracket..." />

      <!-- Bracket -->
      <div v-else-if="bracket" class="space-y-8">
        <!-- Nombre de Categoría -->
        <div class="text-center mb-8">
          <p class="text-3xl font-bold text-yellow-500 uppercase tracking-widest">{{ bracket.categoria_nombre }}</p>
        </div>

        <div v-for="(ronda, idx) in bracket.rondas" :key="idx" class="bg-black p-8 rounded-lg border border-yellow-500 border-opacity-30">
          <h2 class="text-3xl font-bold mb-8 text-yellow-400 text-center uppercase tracking-wide">
            🎯 RONDA {{ ronda.numero_ronda }}
          </h2>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div
              v-for="llave in ronda.llaves"
              :key="llave.id"
              class="rounded-lg p-6 border-2 transition-all hover:shadow-lg hover:shadow-yellow-500/50"
              :class="clasesLlave(llave)"
            >
              <!-- Identificador de Llave -->
              <div class="text-center mb-4 pb-4 border-b border-white border-opacity-30">
                <p class="text-xs font-bold text-yellow-300">{{ llave.llave_identificador || `Llave #${llave.id}` }}</p>
              </div>

              <!-- BYE -->
              <div v-if="llave.es_bye" class="text-center">
                <div class="text-4xl mb-2">📍</div>
                <div class="font-bold text-2xl text-yellow-400">{{ llave.competidor1_apelido }}</div>
                <div v-if="llave.competidor1_regional" class="text-sm text-yellow-200 font-semibold">{{ llave.competidor1_regional }}</div>
                <div class="text-yellow-300 font-semibold mt-2">AVANZA AUTOMÁTICAMENTE</div>
              </div>

              <!-- Combate Normal -->
              <div v-else>
                <!-- Competidor 1 -->
                <div class="mb-4">
                  <div class="font-bold text-2xl mb-1 text-yellow-400">{{ llave.competidor1_apelido }}</div>
                  <div v-if="llave.competidor1_regional" class="text-sm text-yellow-200 font-semibold mb-2">{{ llave.competidor1_regional }}</div>

                  <!-- Rounds -->
                  <div class="flex gap-2">
                    <span
                      v-for="(round, ridx) in llave.rounds"
                      :key="ridx"
                      class="text-xs font-bold px-2 py-1 rounded"
                      :class="claseRound(round)"
                    >
                      R{{ round.numero_round }}: {{ round.ganador_color || '-' }}
                    </span>
                  </div>
                </div>

                <!-- VS -->
                <div class="text-center font-bold text-yellow-400 py-2">VS</div>

                <!-- Competidor 2 -->
                <div class="mt-4">
                  <div class="font-bold text-2xl mb-1 text-yellow-400">{{ llave.competidor2_apelido }}</div>
                  <div v-if="llave.competidor2_regional" class="text-sm text-yellow-200 font-semibold mb-2">{{ llave.competidor2_regional }}</div>

                  <!-- Rounds (igual que arriba) -->
                  <div class="flex gap-2">
                    <span
                      v-for="(round, ridx) in llave.rounds"
                      :key="ridx"
                      class="text-xs font-bold px-2 py-1 rounded"
                      :class="claseRound(round)"
                    >
                      R{{ round.numero_round }}: {{ round.ganador_color || '-' }}
                    </span>
                  </div>
                </div>

                <!-- Ganador -->
                <div v-if="llave.ganador_nombre" class="mt-4 text-center bg-green-600 py-3 rounded font-bold text-xl">
                  ✓ GANADOR: {{ llave.ganador_apelido || llave.ganador_nombre }}
                </div>

                <!-- Estado -->
                <div class="mt-4 text-center text-sm font-semibold">
                  Estado: <span class="text-yellow-300">{{ llave.estado.toUpperCase() }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="text-center py-12 text-xl">
        Selecciona un torneo y categoría para ver el bracket
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useAPI } from '../../composables/useAPI'
import { useSSE } from '../../composables/useSSE'
import Loading from '../../components/Loading.vue'
import { API_URL } from '../../config'

const api = useAPI()
const { connect: connectSSE, disconnect: disconnectSSE } = useSSE()

const torneos = ref([])
const bracket = ref(null)
const torneoSeleccionado = ref('')
const categoriaSeleccionada = ref('')
const loading = ref(false)
const ultimaActualizacion = ref('')
let refreshInterval = null

const categoriasDisponibles = computed(() => {
  const torneo = torneos.value.find(t => t.id === parseInt(torneoSeleccionado.value))
  return torneo?.categorias || []
})

// Guardar selecciones en localStorage
watch(torneoSeleccionado, (newVal) => {
  if (newVal) {
    localStorage.setItem('pantallaPública_torneoId', newVal)
    // Limpiar categoría cuando cambia el torneo
    categoriaSeleccionada.value = ''
  }
})

watch(categoriaSeleccionada, (newVal) => {
  if (newVal) {
    localStorage.setItem('pantallaPública_categoriaId', newVal)
  }
})


const clasesLlave = (llave) => {
  if (llave.es_bye) {
    return 'bg-gray-700 border-yellow-500'
  }

  if (llave.es_tercer_puesto) {
    return 'bg-orange-950 border-orange-500'
  }

  const clases = {
    'pendiente': 'bg-gray-800 border-gray-600',
    'en_curso': 'bg-blue-700 border-blue-500',
    'finalizado': 'bg-gray-900 border-gray-600 text-white'
  }
  return clases[llave.estado] || 'bg-gray-800 border-gray-600'
}

const claseRound = (round) => {
  const clases = {
    'pendiente': 'bg-gray-600 text-gray-100',
    'en_curso': 'bg-blue-600 text-blue-100',
    'finalizado': 'bg-green-600 text-green-100'
  }
  return clases[round.estado] || 'bg-gray-600'
}

const actualizarHora = () => {
  const now = new Date()
  ultimaActualizacion.value = now.toLocaleTimeString('es-CO')
}

const cargarBracket = async () => {
  if (!torneoSeleccionado.value || !categoriaSeleccionada.value) {
    bracket.value = null
    disconnectSSE()
    if (refreshInterval) clearInterval(refreshInterval)
    return
  }

  try {
    loading.value = true
    bracket.value = await api.getBracket(
      torneoSeleccionado.value,
      categoriaSeleccionada.value
    )
    actualizarHora()
    loading.value = false

    // Conectar a SSE para actualizaciones en tiempo real (solo la primera vez)
    const endpoint = `${API_URL}?url=bracket-stream&torneo_id=${torneoSeleccionado.value}&categoria_id=${categoriaSeleccionada.value}`

    connectSSE(endpoint, () => {
      // Simplemente refrescar el bracket
      api.getBracket(torneoSeleccionado.value, categoriaSeleccionada.value)
        .then(b => {
          bracket.value = b
          actualizarHora()
        })
        .catch(err => console.error('Error recargando bracket:', err))
    })
  } catch (err) {
    console.error('Error cargando bracket:', err)
    loading.value = false
  }
}

const cargarTorneos = async () => {
  try {
    torneos.value = await api.getTorneos()
  } catch (err) {
    console.error('Error cargando torneos:', err)
  }
}

onMounted(async () => {
  await cargarTorneos()

  // Restaurar selecciones del localStorage
  const torneoGuardado = localStorage.getItem('pantallaPública_torneoId')
  const categoriaGuardada = localStorage.getItem('pantallaPública_categoriaId')

  if (torneoGuardado && categoriaGuardada) {
    torneoSeleccionado.value = parseInt(torneoGuardado)
    categoriaSeleccionada.value = parseInt(categoriaGuardada)
    await cargarBracket()
  }

  // No actualizar automáticamente, solo mantener SSE activo
  // refreshInterval = setInterval(cargarBracket, 4000)
})

onUnmounted(() => {
  if (refreshInterval) clearInterval(refreshInterval)
  disconnectSSE()
})
</script>
